// 📄 pages/Proyectos/components/IncidentesSection.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { clasificacionDe } from '../modulosConfig.ts';
import { useSearchParams } from 'react-router-dom';
import { Plus, RefreshCw, AlertCircle } from 'lucide-react';
import { useProject } from '../../../context/ProjectContext.tsx';
import { getIncidentes, Incidente, ESTADOS, IncidenteEstado } from '../../../services/incidenteService.ts';
import { SOLUCIONES } from '../solucionesConfig.ts';
import IncidenteCard from './IncidenteCard.tsx';
import IncidenteFormModal from './IncidenteFormModal.tsx';
import IncidenteDetailModal from './IncidenteDetailModal.tsx';

export default function IncidentesSection() {
  const { project, projectId, esCliente, esAdminObservador } = useProject();
  const [incidentes, setIncidentes] = useState<Incidente[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState<IncidenteEstado | 'todos'>('todos');
  const [filtroSolucion, setFiltroSolucion] = useState('todas');
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState<Incidente | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Con SAP Ariba se clasifica por solución; sin SAP, por área del proyecto (ver modulosConfig.ts).
  const { modoArea, opciones: solucionesActivas } = clasificacionDe(project);
  const miembros = project?.miembros || [];

  const loadIncidentes = async () => {
    setLoading(true);
    try {
      const data = await getIncidentes(projectId);
      setIncidentes(data);
      // Si el detalle abierto cambió (p. ej. nuevo comentario), lo refrescamos.
      setSelected(prev => (prev ? data.find(i => i.id === prev.id) || null : null));

      // Deep link desde la alerta de checklist en PRD (?incidenteId=...).
      const incidenteId = searchParams.get('incidenteId');
      if (incidenteId) {
        const match = data.find(i => i.id === incidenteId);
        if (match) setSelected(match);
        setSearchParams(prev => { prev.delete('incidenteId'); return prev; }, { replace: true });
      }
    } catch (err) {
      console.error('Error cargando incidentes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) loadIncidentes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const filtrados = useMemo(() => {
    return incidentes.filter(i => {
      if (filtroEstado !== 'todos' && i.estado !== filtroEstado) return false;
      if (filtroSolucion !== 'todas' && i.solucionRelacionada !== filtroSolucion) return false;
      return true;
    });
  }, [incidentes, filtroEstado, filtroSolucion]);

  const handleSaved = () => {
    setFormOpen(false);
    loadIncidentes();
  };

  if (!project) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value as IncidenteEstado | 'todos')}
            className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
          >
            <option value="todos">Todos los estados</option>
            {ESTADOS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          <select
            value={filtroSolucion}
            onChange={(e) => setFiltroSolucion(e.target.value)}
            className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
          >
            <option value="todas">{modoArea ? 'Todas las áreas' : 'Todas las soluciones'}</option>
            {solucionesActivas.map(slug => (
              <option key={slug} value={slug}>{SOLUCIONES[slug]?.label || slug}</option>
            ))}
          </select>
        </div>

        {!esCliente && !esAdminObservador && (
          <button
            onClick={() => setFormOpen(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-xl text-sm font-semibold text-[#fff] transition-all"
          >
            <Plus className="w-4 h-4" /> Reportar Incidente
          </button>
        )}
      </div>

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
          <p className="text-sm">Cargando incidentes...</p>
        </div>
      ) : filtrados.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">
            {incidentes.length === 0 ? 'Todavía no hay incidentes reportados en este proyecto.' : 'Ningún incidente coincide con el filtro.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtrados.map((inc) => (
            <IncidenteCard key={inc.id} incidente={inc} miembros={miembros} onClick={() => setSelected(inc)} />
          ))}
        </div>
      )}

      {selected && (
        <IncidenteDetailModal
          projectId={projectId}
          incidente={selected}
          miembros={miembros}
          solucionesActivas={solucionesActivas}
          modoArea={modoArea}
          onClose={() => setSelected(null)}
          onUpdated={loadIncidentes}
        />
      )}

      {formOpen && (
        <IncidenteFormModal
          projectId={projectId}
          solucionesActivas={solucionesActivas}
          modoArea={modoArea}
          miembros={miembros}
          onClose={() => setFormOpen(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
